import { useCallback, type MouseEvent as ReactMouseEvent } from "react";

import { CopyButton, usePreloadIntent } from "@stacklet/ui";

import { StatusIcon } from "app/components/icons/StatusIcon";

import { TooltipLink } from "./TooltipLink";

interface CopyButtonWrapperProps {
  enabled: boolean;
  value: string;
  children: React.ReactNode;
  copyLabel?: string;
  truncate?: boolean;
}

const CopyButtonWrapper = ({
  enabled,
  value,
  children,
  copyLabel,
  truncate,
}: CopyButtonWrapperProps) => {
  if (enabled) {
    return (
      <CopyButton
        aria-label={`Copy ${copyLabel ?? "value"}`}
        className="w-full min-w-0 items-center justify-between"
        copyLabel={copyLabel}
        tooltipTarget="icon"
        value={value}
      >
        {children}
      </CopyButton>
    );
  }
  if (truncate) {
    return <div className="w-full min-w-0">{children}</div>;
  }
  return children;
};

interface Props {
  name: string;
  to: string;
  description?: string | null;
  errorMessage?: string;
  copyButton?: boolean;
  copyValue?: string;
  copyLabel?: string;
  truncate?: boolean;
  /**
   * Starts fetching what the linked page will need, once the user has hovered
   * or focused this link long enough to look like they mean to follow it.
   */
  onPreload?: () => void;
}

export function TableLink({
  copyButton = false,
  description,
  errorMessage,
  name,
  to,
  copyValue,
  copyLabel,
  truncate = false,
  onPreload,
}: Props) {
  const tooltipDescription = description || (truncate ? name : "");
  const preloadHandlers = usePreloadIntent(onPreload);

  const handleTableLinkClick = useCallback((e: ReactMouseEvent<Element>) => {
    e.stopPropagation();
  }, []);

  return (
    <CopyButtonWrapper
      copyLabel={copyLabel}
      enabled={copyButton}
      truncate={truncate}
      value={copyValue || name}
    >
      <span
        className={
          truncate
            ? "flex w-full min-w-0 items-center gap-xxs"
            : "inline-flex min-w-0 max-w-full flex-1 items-center gap-xxs"
        }
      >
        {errorMessage ? (
          <span className="leading-none inline-flex shrink-0 items-center [&_svg]:block">
            <StatusIcon message={errorMessage} status="error" />
          </span>
        ) : null}
        <span
          className="min-w-0 max-w-full flex-1 overflow-hidden"
          onClick={handleTableLinkClick}
          {...preloadHandlers}
        >
          <TooltipLink
            description={tooltipDescription}
            to={to}
            // Without an explicit description the tooltip just repeats the
            // name, which is only useful while the name is clipped.
            tooltipOnlyWhenTruncated={!description}
            truncate={truncate}
          >
            {name}
          </TooltipLink>
        </span>
      </span>
    </CopyButtonWrapper>
  );
}
